/**
 * Application translations: English (en) and Kannada (kn).
 * Add new keys under both `en` and `kn` when extending.
 */
const translations = {
  en: {
    sidebar: {
      dashboard: "Dashboard",
      quotations: "Quotation",
      events: "Event",
      billing: "Billing",
      notes: "Note",
      team: "Team",
      projects: "Project",
      policy: "Policy",
      services: "Service",
      checklist: "Checklist",
      adminPanel: "Admin Panel",
      logout: "Logout",
    },
    settings: {
      title: "Settings",
      yourProfile: "Your Profile",
      yourProfileDesc: "Manage your account settings and preferences.",
      companyInfo: "Company Information",
      companyInfoDesc: "Manage your company details and business information.",
      bankDetails: "Bank Details",
      bankDetailsDesc: "Configure your banking information for payments.",
      notificationPrefs: "Your Preferences",
      notificationPrefsDesc: "Configure how you receive notifications.",
      yourPreferences: "Your Preferences",
      yourPreferencesDesc:
        "Appearance, notifications, reminders, and AI settings.",
      notificationsTab: "Notifications",
      remindersTab: "Reminders",
      aiVoice: "Assistant — Name & Voice",
      aiVoiceFemale: "Mira — Female",
      aiVoiceMale: "Mivan — Male",
      aiVoiceHint:
        "Pick your personal assistant. Mira (female) and Mivan (male) are both calm, clear and confident — you just choose the name & voice you prefer.",
      aiPreviewVoice: "Preview Voice",
      security: "Security",
      securityDesc: "Update your password and security settings.",
      aiAssistant: "AI Assistant",
      aiAssistantDesc: "Customize your AI assistant's tone and language.",
      aiPrefsIntro: "Personalize how the AI assistant communicates with you.",
      aiTone: "Conversation Tone",
      aiToneFriendly: "Friendly",
      aiToneProfessional: "Professional",
      aiToneHumorous: "Humorous",
      aiToneMinimalist: "Minimalist",
      aiToneEnthusiastic: "Enthusiastic",
      aiToneHint: "How the AI responds to your questions",
      aiSpeechLang: "Assistant Language",
      aiSpeechLangHint: "Language for speech input",
      superAdmin: "Super Admin",
      superAdminDesc: "Manage administrative settings and permissions.",
      languagePref: "Language Preference",
      languagePrefDesc: "Choose your preferred display language.",
      profileModalTitle: "Profile Settings",
      noImage: "No image",
      noLogo: "No logo",
      changeLogo: "Change logo",
      uploadLogo: "Upload logo",
      noQR: "No QR",
      changeQR: "Change QR",
      uploadQR: "Upload QR",
      companyName: "Company Name",
      companyEmail: "Email",
      companyPhone: "Phone",
      companyAddress: "Address",
      companyGstin: "GSTIN",
      bankName: "Bank Name",
      accountHolder: "Account Holder Name",
      accountNumber: "Account Number",
      branch: "Branch",
      ifsc: "IFSC",
      upiId: "UPI ID",
      upiQr: "UPI QR Code",
      loggedInAs: "Logged in as",
      loginAnother: "Login to Another Account",
      forgotPassword: "I Forgot My Password",
      logoutWarning:
        "Clicking any of these buttons will Logout current account immediately.",
      adminTitle: "Super Admin Authentication",
      adminEmail: "Admin Email",
      adminPassword: "Password",
      authenticate: "Authenticate",
      authInstruction:
        "This area is for authorized admins only. Do not attempt to access if you are not an admin. Unauthorized attempts may result in your account being blocked or terminated.",
      installApp: "Install App",
      installApplyDesc:
        "Download the MIVENT Android app for a better mobile experience.",
      downloadApk: "Download APK",
      installDesc:
        "Install MIVENT as an app/software for a full-screen experience with faster access.",
      installNow: "Install Now",
      installManualHint:
        "To install, use your browser's Install app option (look for the install icon in the address bar or browser menu).",
    },
    notifications: {
      title: "Your Preferences",
      overallNotifications: "System notification",
      muteSound: "Mute all sounds",
      eventNotifications: "Record created notification",
      eventReminder: "Event Reminder",
      remindBefore: "Remind Event before",
      projectReminder: "Project Reminder",
      remindProjectBefore: "Remind Project before",
      custom: "Custom",
      minutes: "Minutes",
      hours: "Hours",
      days: "Days",
      customHint:
        "Custom reminders support any value from 1 minute up to 15 days before the event.",
      customProjectHint:
        "Custom reminders support any value from 1 minute up to 15 days before the project.",
      languagePref: "Language Preference",
      pushTitle: "Push Notifications",
      pushDesc:
        "Get event/project reminders and announcements on this device even when the app is closed.",
      pushUnsupported:
        "This browser doesn't support push notifications. Try Chrome, Edge, or Firefox.",
      pushBusy: "Please wait...",
      pushEnable: "Enable Push Notifications",
      pushDisable: "Disable Push Notifications",
    },
    appearance: {
      title: "Appearance",
      light: "Light",
      dark: "Dark",
      system: "System",
      systemHint: "Follow your device's light or dark setting automatically.",
      hint: "Switch between light and dark mode. Your choice is saved on this device.",
      toggleAria: "Toggle dark mode",
    },
    common: {
      save: "Save",
      cancel: "Cancel",
      clear: "Clear",
      english: "English",
      kannada: "ಕನ್ನಡ",
      language: "Language",
      export: "Export",
      edit: "Edit",
      delete: "Delete",
      reset: "Reset",
      saveDraft: "Save Draft",
      saveChanges: "Save Changes",
      showing: "Showing",
      to: "to",
      of: "of",
      rowsPerPage: "Rows per page:",
      from: "From",
      toDate: "To",
      selectAll: "Select All",
      showColumns: "Show Columns",
      all: "All",
      own: "Own",
      sub: "Sub",
      suborder: "Suborder",
      status: "Status",
      on: "On",
      off: "Off",
      date: "Date",
      notes: "Notes",
      name: "Name",
      phone: "Phone",
      email: "Email",
      amount: "Amount",
      actions: "Actions",
      noCol: "No.",
      pending: "Pending",
      paid: "Paid",
      firstPage: "First Page",
      prevPage: "Previous Page",
      nextPage: "Next Page",
      lastPage: "Last Page",
      prev: "Previous",
      next: "Next",
      orderType: "Order",
      client: "Client",
      services: "Services",
      location: "Location",
      viewAll: "View All",
      allStatus: "All Status",
      allOrders: "All Orders",
      addItem: "Add Item",
      subtotal: "Subtotal:",
      total: "Total:",
      addCharge: "+ Add Charge",
      charge: "Charge",
      chargeDesc: "Charge description",
      qty: "Qty",
      unitPrice: "Unit Price (₹)",
      lineTotal: "Line Total (₹)",
      noServices: "No services listed",
      sectionCharges: "Additional Charges",
      sectionTerms: "Terms & Conditions",
      noTerms: "No terms and conditions available.",
      noTermsHint: "Go to the Policy page to add your terms and conditions.",
      showInPdf: "Show in PDF",
      showDescPdf: "Show Description in PDF",
      showPaymentPdf: "Show Payment Details in PDF",
      showQrPdf: "Show QR Code in PDF",
    },
    navbar: {
      settings: "Settings",
      logout: "Logout",
      help: "Help & Support",
      tutorial: "Tutorial",
      plan: "Your Plan",
      paymentHistory: "Payment History",
    },
    dashboard: {
      title: "Dashboard",
      overview: "Overview",
      recentActivity: "Recent Activity",
      welcomeBack: "Welcome back",
      todaySummary: "Here's what's happening with your Business today.",
      filterAll: "All",
      filterWeek: "Week",
      filterMonth: "Month",
      filterYear: "Year",
      filterCustom: "Custom",
      filterFrom: "From",
      filterTo: "To",
      orderOwn: "Own",
      orderSub: "Sub order",
      statTotalEvents: "Total Events",
      statTotalBills: "Total Bills",
      statTotal: "Total",
      statGross: "Gross billed amount",
      statPending: "Pending",
      statOutstanding: "Outstanding amount",
      statPaid: "Paid",
      statCollected: "Collected amount",
      statProfit: "Profit",
      statProfitSub: "Bills - charges - team payouts",
      upcomingEvents: "Upcoming Events",
      eventCalendar: "Event Calendar",
      calendarHint: "Select a day to view its events",
      showUpcoming: "Upcoming",
      createEvent: "Create Event",
      quickActions: "Quick Actions",
      noUpcomingEvents: "No upcoming events",
      noUpcomingHint: "Add events from the Events page",
      dateTBA: "Date TBA",
      listView: "List",
      calendarView: "Calendar",
      viewToggleLabel: "Upcoming events view",
      today: "Today",
      tomorrow: "Tomorrow",
      overdue: "Overdue",
      inDays: "In {count}d",
      noEventsThisMonth: "No events this month",
      eventsThisMonth: "{count} this month",
      undatedNote: "{count} without date",
      noEventsOnDay: "No events",
      eventsOnDay: "{count} events",
      eventSingular: "event",
      untitledEvent: "Untitled event",
      calendarPreviewEmpty: "No events scheduled",
      calendarPreviewHint: "Click to view day",
      calendarPreviewMore: "+{count} more · click to view",
      noEventsOnDayHint: "Nothing scheduled for this day.",
      actionQuotation: "Quotation",
      actionEvent: "Event",
      actionBilling: "Billing",
      actionProject: "Project",
      actionTeam: "Team",
      actionNotes: "Notes",
      actionService: "Service",
    },
    pages: {
      quotations: "Quotations",
      quotationsSubtitle: "Manage and track client quotations",
      events: "Events",
      eventsSubtitle: "Manage and track client events and services",
      billing: "Billing & Invoices",
      billingSubtitle: "Manage and track client invoices and payments",
      notes: "Notes",
      team: "Team",
      teamSubtitle: "Manage all your team members here",
      projects: "Projects",
      projectsSubtitle: "Manage and track all your business projects",
      policy: "Policy",
      services: "Services & Packages",
      servicesSubtitle: "Manage your service catalog and packages",
      payment: "Payment",
      analytics: "Analytics",
    },
    profile: {
      title: "Profile Settings",
      fullName: "Full Name",
      email: "Email",
      phone: "Phone",
      designation: "Designation / Role",
      uploadPhoto: "Upload photo",
      changePhoto: "Change photo",
    },
    reminder: {
      preset_120: "2 hours before",
      preset_720: "12 hours before",
      preset_1440: "24 hours before",
      preset_2880: "2 days before",
    },
    note: {
      title: "Notes",
      subtitle: "Keep track of your ideas and tasks",
      tabNotes: "Notes",
      tabArchived: "Archived",
      searchPlaceholder: "Search notes...",
      addNote: "New Note",
      takeANote: "Take a note...",
      typeText: "Text",
      typeChecklist: "Checklist",
      titlePlaceholder: "Title",
      addItem: "Add item...",
      saveNote: "Save Note",
      pinned: "Pinned",
      others: "Others",
      loading: "Loading notes...",
      noArchived: "No archived notes",
      noNotes: "No notes yet",
      archivedEmpty: "Your archived notes will appear here",
      notesEmpty: 'Click "Take a note..." to create your first note',
      edit: "Edit",
      archive: "Archive",
      unarchive: "Unarchive",
      delete: "Delete",
      noteContent: "Note content...",
      saveChanges: "Save Changes",
    },
    policy: {
      title: "Terms & Conditions Policy",
      subtitle: "Manage all your terms and conditions policies",
      addTerm: "Add Term",
      totalTerms: "Total Terms",
      categories: "Categories",
      uncategorized: "Uncategorized",
      groupLabel: "Group/Category",
      selectGroup: "-- Select group --",
      createNewGroup: "+ Create new group...",
      enterTerm: "Enter Terms & Conditions",
      editTerm: "Edit Term",
      update: "Update",
      addTermBtn: "Add Term",
      savedTerms: "Saved Terms & Conditions",
      allGroups: "All Groups",
      searchTerms: "Search terms...",
      noTerms: "No terms and conditions added yet.",
      noTermsHint: "Use the form above to add your first term.",
      noMatch: "No terms match your search or filter.",
      noMatchHint: "Try adjusting your search or filter criteria.",
      contentHeader: "Content",
      actionsHeader: "Actions",
      noHeader: "No.",
      writeTerm: "Write terms and conditions here...",
      expandAll: "Expand all",
      collapseAll: "Collapse all",
      expandGroup: "Expand group",
      collapseGroup: "Collapse group",
    },
    services: {
      subtitle: "Manage your all services and category offerings",
      addNew: "Add New Service",
      totalServices: "Total Services",
      totalCategories: "Total Categories",
      totalValue: "Total Value",
      averagePrice: "Average Price",
      noFound: "No services found",
      noFoundHint: "Try adjusting your filters or add a new service",
      categoryLabel: "Category:",
      allCategories: "All Categories",
      startingAt: "Starting at",
      showing: "Showing",
      to: "to",
      of: "of",
      servicesLabel: "services",
      perPage: "Per page:",
      editTitle: "Edit Service",
      addTitle: "Add New Service",
      nameLabel: "Service Name",
      categoryFormLabel: "Category",
      priceLabel: "Base Price (₹)",
      descLabel: "Description (optional)",
      selectCategory: "-- Select Category --",
      addNewCategory: "+ Add New Category",
      enterCategoryName: "Enter new category name",
      nameRequired: "Service name is required",
      categoryRequired: "Category is required",
      priceRequired: "A valid price is required",
      update: "Update",
      save: "Save",
    },
    event: {
      newEvent: "New Event",
      statTotal: "Total Events",
      statCompleted: "Completed",
      statCancelled: "Cancelled",
      statScheduled: "Scheduled / In-progress",
      searchPlaceholder: "Search events by client, ID, service or location...",
      statusScheduled: "Scheduled",
      statusInProgress: "In Progress",
      statusCompleted: "Completed",
      statusCancelled: "Cancelled",
      orderLabel: "Order",
      colEventId: "Event ID",
      colQuotNo: "Quotation No.",
      colTeam: "Team",
      emptyTitle: "No events found",
      emptyHint: "Create your first event to get started",
      eventsLabel: "events",
      createTitle: "Create Event",
      labelEventId: "Event ID",
      labelQuotationId: "Quotation ID",
      searchQuotation: "Search quotation by ID or client name",
      noQuotations: "No quotations available",
      labelClientName: "Client Name *",
      enterClientName: "Enter client name",
      labelClientPhone: "Client Phone *",
      labelClientEmail: "Client Email",
      sectionServices: "Services & Schedule",
      addService: "Add Service",
      labelServiceType: "Service Type",
      labelTime: "Time",
      enterLocation: "Enter event location",
      notesPlaceholder: "Additional notes for this service",
      sameAsAbove: "Same date/time/location as above",
      sectionTeam: "Team Assignment",
      notifyAssignees: "Notify members",
      notifyAssigneesHint:
        "Send notification to assigned members (ON by default). Turn OFF to skip notifying them.",
      colReferredBy: "Invited By",
      labelReferredBy: "Invited By",
      referredByPlaceholder:
        "Enter who invited this sub-order (e.g. assigner name)",
      colInvitedBy: "Invited By",
      labelInvitedBy: "Invited By",
      invitedByPlaceholder:
        "Enter who invited this sub-order (e.g. assigner name)",
      sectionInvitedBy: "Invited By",
      noTeamMembers:
        "No team members found. Add team members on the Team page.",
      createEvent: "Create Event",
      viewQuotation: "Quotation:",
      labelNA: "NA",
      labelCreated: "Created",
      labelCreatedDate: "Created Date:",
      labelOrderTypeView: "Order Type:",
      serviceCount: "Services",
      svcNotes: "Notes:",
      teamMembersSection: "Team Members",
      pingTeam: "Ping Team",
      pinging: "Sending...",
      pingTeamHint: "Resend notification to all assigned members",
      pingMemberHint: "Resend notification to",
    },
    quotation: {
      newQuotation: "New Quotation",
      statTotal: "Total Quotations",
      statRejected: "Rejected",
      statRejectedValue: "Rejected Value",
      statPending: "Pending",
      statPendingValue: "Pending Value",
      statApproved: "Approved",
      statApprovedValue: "Approved Value",
      statRejValue: "Rejected Value",
      statPendValue: "Pending Value",
      statAppValue: "Approved Value",
      statTotalValue: "Total Value",
      searchPlaceholder: "Search quotations by ID, Name or Date...",
      statusPending: "Pending",
      statusApproved: "Approved",
      statusRejected: "Rejected",
      colQuotId: "Quotation ID",
      colEventDate: "Event Date",
      colAmount: "Amount",
      colMainEventDate: "Main Event Date",
      emptyTitle: "No quotations found",
      emptyHint: "Create your first quotation to get started",
      quotationsLabel: "quotations",
      createTitle: "Create Quotation",
      editTitle: "Edit Quotation",
      orderOwn: "Own",
      orderSub: "Sub",
      labelClientName: "Client Name *",
      labelClientPhone: "Client Phone *",
      labelClientEmail: "Client Email",
      labelEventDate: "Main Event Date",
      enterClientName: "Enter client name",
      enterNumbersOnly: "Enter numbers only",
      colService: "Service",
      colQty: "Quantity",
      colPrice: "Price (₹)",
      colTotal: "Total",
      colDescOpt: "Description (optional)",
      additionalDetails: "Additional details about this service",
      termsSelected: "term(s) selected",
      createQuotation: "Create Quotation",
      viewTitle: "Quotation",
      colUnitPrice: "Unit Price (₹)",
      colLineTotal: "Line Total (₹)",
      approveBtn: "Approve",
      rejectBtn: "Reject",
      colQtyShort: "Qty",
    },
    billing: {
      newBill: "New Bill",
      statTotalAmount: "Total Amount",
      statPartialPayment: "Partial Payment",
      statPending: "Pending Payment",
      statPaid: "Total Paid",
      searchPlaceholder:
        "Search invoices by ID, Name, Date, Quotation ID, Service, or Amount...",
      statusPending: "Pending",
      statusPartial: "Partial",
      statusPaid: "Paid",
      statusOverdue: "Overdue",
      orderOwn: "Own",
      orderSub: "Sub",
      colInvoiceId: "Invoice ID",
      colEventId: "Event ID",
      colQuotId: "Quotation ID",
      colBalance: "Balance",
      emptyTitle: "No invoices found",
      emptyHint: "Try adjusting your filters",
      invoicesLabel: "invoices",
      colTotalAmt: "Total Amount",
      colPaymentMethod: "Payment Method",
      viewBtn: "View",
      recordPaymentTitle: "Record Payment",
      labelTotalAmt: "Total Amount:",
      labelAlreadyPaid: "Already Paid:",
      labelOutstanding: "Outstanding Balance:",
      labelPaymentAmt: "Payment Amount (₹) *",
      labelPaymentMethod: "Payment Method *",
      labelPaymentDate: "Payment Date *",
      methodBankTransfer: "Bank Transfer",
      methodUpi: "UPI",
      methodCash: "Cash",
      methodCheque: "Cheque",
      recordPaymentBtn: "Record Payment",
      createTitle: "Create Invoice",
      editTitle: "Edit Invoice",
      qtnMode: "QTN ID",
      evntMode: "EVNT ID",
      labelInvoiceId: "Invoice ID",
      labelIssueDate: "Issue Date",
      labelClientName: "Client Name *",
      labelClientPhone: "Client Phone *",
      labelClientEmail: "Client Email",
      searchQuotation: "Search quotation by ID or client name",
      searchEvent: "Search event by ID or client name",
      enterClientName: "Enter client name",
      enterNumbersOnly: "Enter numbers only",
      noQuotations: "No quotations available",
      noEvents: "No events available",
      sectionServices: "Services",
      createInvoice: "Create Invoice",
      labelIssueDate2: "Issue Date:",
      noCharges: "No additional charges.",
      labelTotalAmt2: "Total Amount:",
      labelAmountPaid: "Amount Paid:",
      labelBalanceDue: "Balance Due:",
      repaymentHistory: "Repayment History",
      labelRef: "Ref:",
      noPayments: "No payments recorded yet.",
      downloadPdf: "Download PDF",
      sendEmail: "Send Email",
      noServiceItems: "No service items available.",
      colEventIdView: "Event ID:",
      colQuotIdView: "Quotation ID:",
    },
    team: {
      addMember: "Add Team Member",
      statTotal: "Total Members",
      statPayment: "Total Payment",
      statPaid: "Paid Amount",
      statPending: "Pending Amount",
      searchPlaceholder: "Search by name, role or phone...",
      emptyState: "No team members found.",
      emptyTitle: "No team members found.",
      editMember: "Edit Team Member",
      deleteMember: "Delete",
      addTitle: "Add Team Member",
      addMemberTitle: "Add Team Member",
      editTitle: "Edit Team Member",
      labelName: "Name",
      labelPhone: "Phone",
      labelEmail: "Email",
      labelRole: "Role",
      addPaymentTitle: "Add Payment",
      labelEvent: "Event",
      useCustomEvent: "Use custom event name",
      selectEvent: "Select Event",
      noEventsForMember: "No events for this member",
      enterEventName: "Enter event name",
      labelAmount: "Amount (₹)",
      labelPaymentDate: "Payment Date",
      labelStatus: "Status",
      labelNotes: "Notes (Optional)",
      statusPending: "Pending",
      statusPartial: "Partial",
      statusPaid: "Paid",
      notesPlaceholder: "Add any notes about this payment...",
      addPaymentBtn: "Add Payment",
      paymentsTitle: "Payments",
      colEventId: "Event ID",
      colEvent: "Event",
      colPayment: "Payment",
      colPaymentDate: "Payment Date",
      colStatus: "Status",
      colNotes: "Notes",
      colActions: "Actions",
      markPaid: "Mark Paid",
      noPayments: "No payments recorded for this member.",
      pageLabel: "Page",
      ofLabel: "of",
    },
    project: {
      newProject: "New Project",
      searchPlaceholder: "Search projects...",
      projectsLabel: "projects",
      colClient: "Client:",
      colDeadline: "Deadline:",
      colDescription: "Description:",
      noDescription: "No description provided",
      noDescHint: "No description provided",
      viewDetails: "View",
      emptyState: "No projects found matching your criteria.",
      emptyTitle: "No projects found matching your criteria.",
      addTitle: "Add New Project",
      viewTitle: "Project Details",
      editTitle: "Edit Project",
      selectEvent: "Select Existing Event",
      searchEvent: "Search event by ID or client name",
      eventHint:
        "Selecting an event prefills project and client names. You can still edit both fields.",
      labelProjectName: "Project Name *",
      labelClientName: "Client Name *",
      labelStatus: "Project Status *",
      labelDeadline: "Deadline *",
      labelDeadlineTime: "Deadline Time",
      labelDescription: "Description:",
      descPlaceholder: "Project description...",
      createProject: "Create Project",
      deleteProject: "Delete",
      editProject: "Edit Project",
      close: "Close",
      labelClient: "Client",
      labelDeadlineView: "Deadline",
      page: "Page",
    },

    planCard: {
      silver: "Silver",
      gold: "Gold",
      unlimitedFeatures: "Unlimited features",
      limitedFeatures: "Limited Features",
      save: "Save",
      off: "OFF",
      perYear: "Per Year",
      perMonth: "Per Month",
      contactUs: "Contact Us",
      activate: "Activate",
      upgradeTo: "Upgrade to",
      downgradeTo: "Downgrade to",
      managePlan: "Manage Plan",
      confirmUpgrade: "Are you sure you want to upgrade to the Gold plan?",
      confirmDowngrade:
        "Are you sure you want to downgrade to the Silver plan?",
      confirmChange:
        "Are you sure you want to change your plan from {from} to {to}?",
      alreadyActive: "The {plan} plan is already active on your account.",
      dialogUpgradeTitle: "Upgrade Plan",
      dialogDowngradeTitle: "Downgrade Plan",
      dialogChangePlanTitle: "Change Plan",
      dialogActivePlanTitle: "Plan Active",
      confirm: "Confirm",
      upgrade: "Upgrade",
      downgrade: "Downgrade",
      ok: "OK",
    },

    payment: {
      silver: "Silver",
      gold: "Gold",
      silverPlan: "Silver Plan",
      goldPlan: "Gold Plan",
      silverFeature1: "Add Unlimited Services",

      silverFeature3: "Add Unlimited Team Members",
      silverFeature4: "Unlimited Events Creation and Tracking",
      silverFeature5: "Unlimited Projects Creation and Tracking",
      goldFeature1: "All Silver Benefits",
      goldFeature2: "Unlimited policy Creation",
      goldFeature3: "High priority customer support",
      goldFeature4: "Unlimited Bill Creation and Tracking",
      goldFeature5: "Unlimited Quotation Creation and Tracking",
      periodText: "per Year (incl. all tax and fees)",
      billingText: "Annual billing",
      formName: "Name",
      formEmail: "Email",
      formPhone: "Phone",
      proceedPayment: "Proceed to Payment",
      processing: "Processing...",
      paymentSuccess: "Payment successful!",
      redirecting: "Redirecting to dashboard...",
      configError: "Payment gateway is not configured. Please contact support.",
      save: "Save",
      back: "Back",
      managePlan: "Manage Plan",
      orderSummary: "Order Summary",
      selectedPlan: "Selected Plan",
      billingCycle: "Billing Cycle",
      annual: "Annual",
      billingInfo: "Billing Information",
      billingInfoDesc: "Razorpay will use these details for the receipt.",
      payableAmt: "Payable Amount",
      payableAmtSub: "(incl. all tax and fees)",
      iAgree: "I agree to the",
      termsAndConds: "Terms and Conditions",
      pciCompliant: "PCI-DSS Compliant",
      securePowered: "Secure payment powered by Razorpay.",
      subActive: "Subscription Active!",
      planActivated:
        "Your {plan} plan has been successfully activated. Enjoy full access to all features.",
      goDashboard: "Go to Dashboard",
      initializing: "Initializing...",
      securelyPay: "Securely Pay",
    },
  },

  kn: {
    sidebar: {
      dashboard: "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
      quotations: "ಕೋಟೇಶನ್",
      events: "ಕಾರ್ಯಕ್ರಮ",
      billing: "ಬಿಲ್ಲಿಂಗ್",
      notes: "ಟಿಪ್ಪಣಿ",
      team: "ತಂಡ",
      projects: "ಯೋಜನೆ",
      policy: "ನಿಯಮ",
      services: "ಸೇವೆ",
      checklist: "ಚೆಕ್ಲಿಸ್ಟ್",
      adminPanel: "ಅಡ್ಮಿನ್ ಪ್ಯಾನಲ್",
      logout: "ಲಾಗ್‌ಔಟ್",
    },
    settings: {
      title: "ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
      yourProfile: "ನಿಮ್ಮ ಪ್ರೊಫೈಲ್",
      yourProfileDesc: "ನಿಮ್ಮ ಖಾತೆ ಸೆಟ್ಟಿಂಗ್‌ಗಳು ಮತ್ತು ಆದ್ಯತೆಗಳನ್ನು ನಿರ್ವಹಿಸಿ.",
      companyInfo: "ಕಂಪನಿ ಮಾಹಿತಿ",
      companyInfoDesc:
        "ನಿಮ್ಮ ಕಂಪನಿ ವಿವರಗಳು ಮತ್ತು ವ್ಯವಹಾರ ಮಾಹಿತಿಯನ್ನು ನಿರ್ವಹಿಸಿ.",
      bankDetails: "ಬ್ಯಾಂಕ್ ವಿವರಗಳು",
      bankDetailsDesc: "ಪಾವತಿಗಳಿಗಾಗಿ ನಿಮ್ಮ ಬ್ಯಾಂಕಿಂಗ್ ಮಾಹಿತಿಯನ್ನು ಸೆಟ್ ಮಾಡಿ.",
      notificationPrefs: "ನಿಮ್ಮ ಆದ್ಯತೆಗಳು",
      notificationPrefsDesc:
        "ನೀವು ಅಧಿಸೂಚನೆಗಳನ್ನು ಹೇಗೆ ಸ್ವೀಕರಿಸುತ್ತೀರಿ ಎಂಬುದನ್ನು ಸೆಟ್ ಮಾಡಿ.",
      yourPreferences: "ನಿಮ್ಮ ಆದ್ಯತೆಗಳು",
      yourPreferencesDesc:
        "ರೂಪ, ಅಧಿಸೂಚನೆಗಳು, ಜ್ಞಾಪನೆಗಳು ಮತ್ತು AI ಸೆಟ್ಟಿಂಗ್‌ಗಳು.",
      notificationsTab: "ಅಧಿಸೂಚನೆಗಳು",
      remindersTab: "ಜ್ಞಾಪನೆಗಳು",
      aiVoice: "ಸಹಾಯಕ — ಹೆಸರು ಮತ್ತು ಧ್ವನಿ",
      aiVoiceFemale: "ಮೀರಾ — ಮಹಿಳೆ",
      aiVoiceMale: "ಮಿವಾನ್ — ಪುರುಷ",
      aiVoiceHint:
        "ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಸಹಾಯಕರನ್ನು ಆರಿಸಿ. ಮೀರಾ (ಮಹಿಳೆ) ಮತ್ತು ಮಿವಾನ್ (ಪುರುಷ) ಇಬ್ಬರೂ ಶಾಂತ, ಸ್ಪಷ್ಟ ಮತ್ತು ಆತ್ಮವಿಶ್ವಾಸದಿಂದ ಮಾತನಾಡುತ್ತಾರೆ.",
      aiPreviewVoice: "ಧ್ವನಿ ಪೂರ್ವವೀಕ್ಷಣೆ",
      security: "ಸುರಕ್ಷತೆ",
      securityDesc: "ನಿಮ್ಮ ಪಾಸ್‌ವರ್ಡ್ ಮತ್ತು ಭದ್ರತಾ ಸೆಟ್ಟಿಂಗ್‌ಗಳನ್ನು ನಿರ್ವಹಿಸಿ.",
      superAdmin: "ಸೂಪರ್ ಅಡ್ಮಿನ್",
      superAdminDesc: "ಆಡಳಿತ ಸೆಟ್ಟಿಂಗ್‌ಗಳು ಮತ್ತು ಅನುಮತಿಗಳನ್ನು ನಿರ್ವಹಿಸಿ.",
      languagePref: "ಭಾಷೆ ಆದ್ಯತೆ",
      languagePrefDesc: "ನಿಮ್ಮ ಆದ್ಯತೆಯ ಭಾಷೆಯನ್ನು ಆರಿಸಿ.",
      profileModalTitle: "ಪ್ರೊಫೈಲ್ ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
      noImage: "ಯಾವುದೇ ಚಿತ್ರವಿಲ್ಲ",
      noLogo: "ಯಾವುದೇ ಲೋಗೋ ಇಲ್ಲ",
      changeLogo: "ಲೋಗೋ ಬದಲಾಯಿಸಿ",
      uploadLogo: "ಲೋಗೋ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ",
      noQR: "QR ಇಲ್ಲ",
      changeQR: "QR ಬದಲಾಯಿಸಿ",
      uploadQR: "QR ಅಪ್‌ಲೋಡ್ ಮಾಡಿ",
      companyName: "ಕಂಪನಿ ಹೆಸರು",
      companyEmail: "ಇಮೇಲ್",
      companyPhone: "ಫೋನ್",
      companyAddress: "ವಿಳಾಸ",
      companyGstin: "GSTIN",
      bankName: "ಬ್ಯಾಂಕ್ ಹೆಸರು",
      accountHolder: "ಖಾತೆ ಹೊಂದಿರುವವರ ಹೆಸರು",
      accountNumber: "ಖಾತೆ ನಂಖ್ಯೆ",
      branch: "ಶಾಖೆ",
      ifsc: "IFSC",
      upiId: "UPI ಐಡಿ",
      upiQr: "UPI QR ಕೋಡ್",
      loggedInAs: "ಲಾಗಿನ್ ಆಗಿರುವುದು",
      loginAnother: "ಬೇರೆ ಖಾತೆಗೆ ಲಾಗಿನ್ ಮಾಡಿ",
      forgotPassword: "ನನ್ನ ಪಾಸ್‌ವರ್ಡ್ ಮರೆತಿದ್ದೇನೆ",
      logoutWarning:
        "ಈ ಯಾವುದೇ ಬಟನ್ ಕ್ಲಿಕ್ ಮಾಡಿದರೆ ಈಗಿನ ಖಾತೆ ತಕ್ಷಣ ಲಾಗ್‌ಔಟ್ ಆಗುತ್ತದೆ.",
      adminTitle: "ಸೂಪರ್ ಅಡ್ಮಿನ್ ದೃಢೀಕರಣ",
      adminEmail: "ಅಡ್ಮಿನ್ ಇಮೇಲ್",
      adminPassword: "ಪಾಸ್‌ವರ್ಡ್",
      authenticate: "ದೃಢೀಕರಿಸಿ",
      authInstruction:
        "ಈ ಪ್ರದೇಶವು ಅಧಿಕೃತ ಅಡ್ಮಿನ್‌ಗಳಿಗೆ ಮಾತ್ರ. ನೀವು ಅಡ್ಮಿನ್ ಅಲ್ಲದಿದ್ದರೆ ಪ್ರವೇಶಿಸಲು ಯತ್ನಿಸಬೇಡಿ. ಅನಧಿಕೃತ ಪ್ರಯತ್ನಗಳು ನಿಮ್ಮ ಖಾತೆಯನ್ನು ಬ್ಲಾಕ್ ಅಥವಾ ಟರ್ಮಿನೇಟ್ ಮಾಡಬಹುದು.",
      installApp: "ಅಪ್ಲಿಕೇಶನ್ ಸ್ಥಾಪಿಸಿ",
      installApplyDesc:
        "ಉತ್ತಮ ಮೊಬೈಲ್ ಅನುಭವಕ್ಕಾಗಿ MIVENT Android ಅಪ್ಲಿಕೇಶನ್ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ.",
      downloadApk: "APK ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ",
      installDesc:
        "ವೇಗದ ಪ್ರವೇಶ ಮತ್ತು ಪೂರ್ಣ ಪರದೆ ಅನುಭವಕ್ಕಾಗಿ MIVENT ಅನ್ನು ಅಪ್ಲಿಕೇಶನ್ ಆಗಿ ಸ್ಥಾಪಿಸಿ.",
      installNow: "ಈಗ ಸ್ಥಾಪಿಸಿ",
      installManualHint: "ಸ್ಥಾಪಿಸಲು, ನಿಮ್ಮ ಬ್ರೌಸರ್‌ನ Install app ಆಯ್ಕೆ ಬಳಸಿ.",
    },
    notifications: {
      title: "ನಿಮ್ಮ ಆದ್ಯತೆಗಳು",
      overallNotifications: "ಸಿಸ್ಟಮ್ ಅಧಿಸೂಚನೆ",
      muteSound: "ಎಲ್ಲಾ ಶಬ್ದಗಳನ್ನು ಮ್ಯೂಟ್ ಮಾಡಿ",
      eventNotifications: "ರೆಕಾರ್ಡ್ ರಚಿಸಿದ ಅಧಿಸೂಚನೆ",
      eventReminder: "ಕಾರ್ಯಕ್ರಮ ಜ್ಞಾಪನೆ",
      remindBefore: "ಕಾರ್ಯಕ್ರಮದ ಮೊದಲು ಜ್ಞಾಪಿಸಿ",
      projectReminder: "ಯೋಜನೆ ಜ್ಞಾಪನೆ",
      remindProjectBefore: "ಯೋಜನೆಯ ಮೊದಲು ಜ್ಞಾಪಿಸಿ",
      custom: "ಕಸ್ಟಮ್",
      minutes: "ನಿಮಿಷಗಳು",
      hours: "ಗಂಟೆಗಳು",
      days: "ದಿನಗಳು",
      customHint:
        "ಕಸ್ಟಮ್ ಜ್ಞಾಪನೆಗಳು ಕಾರ್ಯಕ್ರಮದ 1 ನಿಮಿಷದಿಂದ 15 ದಿನಗಳ ಮೊದಲು ಯಾವುದೇ ಮೌಲ್ಯವನ್ನು ಬೆಂಬಲಿಸುತ್ತವೆ.",
      customProjectHint:
        "ಕಸ್ಟಮ್ ಜ್ಞಾಪನೆಗಳು ಯೋಜನೆಯ 1 ನಿಮಿಷದಿಂದ 15 ದಿನಗಳ ಮೊದಲು ಯಾವುದೇ ಮೌಲ್ಯವನ್ನು ಬೆಂಬಲಿಸುತ್ತವೆ.",
      languagePref: "ಭಾಷೆ ಆದ್ಯತೆ",
      pushTitle: "ಪುಶ್ ಅಧಿಸೂಚನೆಗಳು",
      pushDesc:
        "ಅಪ್ಲಿಕೇಶನ್ ಮುಚ್ಚಿದ್ದರೂ ಈ ಸಾಧನದಲ್ಲಿ ಕಾರ್ಯಕ್ರಮ/ಯೋಜನೆ ಜ್ಞಾಪನೆಗಳನ್ನು ಪಡೆಯಿರಿ.",
      pushUnsupported:
        "ಈ ಬ್ರೌಸರ್ ಪುಶ್ ಅಧಿಸೂಚನೆಗಳನ್ನು ಬೆಂಬಲಿಸುವುದಿಲ್ಲ. Chrome, Edge, ಅಥವಾ Firefox ಬಳಸಿ.",
      pushBusy: "ದಯವಿಟ್ಟು ನಿರೀಕ್ಷಿಸಿ...",
      pushEnable: "ಪುಶ್ ಅಧಿಸೂಚನೆಗಳನ್ನು ಸಕ್ರಿಯಗೊಳಿಸಿ",
      pushDisable: "ಪುಶ್ ಅಧಿಸೂಚನೆಗಳನ್ನು ನಿಷ್ಕ್ರಿಯಗೊಳಿಸಿ",
    },
    appearance: {
      title: "ಗೋಚರತೆ",
      light: "ಬೆಳಕು",
      dark: "ಗಾಢ",
      system: "ಸಿಸ್ಟಮ್",
      systemHint:
        "ನಿಮ್ಮ ಸಾಧನದ ಬೆಳಕು ಅಥವಾ ಗಾಢ ಸೆಟ್ಟಿಂಗ್ ಅನ್ನು ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಅನುಸರಿಸಿ.",
      hint: "ಬೆಳಕು ಮತ್ತು ಗಾಢ ಮೋಡ್ ನಡುವೆ ಬದಲಿಸಿ. ನಿಮ್ಮ ಆಯ್ಕೆಯನ್ನು ಈ ಸಾಧನದಲ್ಲಿ ಉಳಿಸಲಾಗುತ್ತದೆ.",
      toggleAria: "ಗಾಢ ಮೋಡ್ ಟಾಗಲ್ ಮಾಡಿ",
    },
    common: {
      save: "ಉಳಿಸಿ",
      cancel: "ರದ್ದು",
      clear: "ತೆರವು",
      english: "English",
      kannada: "ಕನ್ನಡ",
      language: "ಭಾಷೆ",
      export: "ಎಕ್ಸ್ಪೋರ್ಟ್",
      edit: "ಎಡಿಟ್",
      delete: "ಅಳಿಸಿ",
      reset: "ರೀಸೆಟ್",
      saveDraft: "ಡ್ರಾಫ್ಟ್",
      saveChanges: "ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ",
      showing: "ತೋರಿಸಲಾಗುತ್ತಿದೆ",
      to: "ರಿಂದ",
      of: "ಒಟ್ಟು",
      rowsPerPage: "ಪ್ರತಿ ಪುಟ ಸಾಲುಗಳು:",
      from: "ಇಂದ",
      toDate: "ವರೆಗೆ",
      selectAll: "ಎಲ್ಲ ಆಯ್ಕೆ ಮಾಡಿ",
      showColumns: "ಕಾಲಂಗಳನ್ನು ತೋರಿಸಿ",
      all: "ಎಲ್ಲ",
      own: "ಸ್ವಂತ",
      sub: "ಉಪ",
      suborder: "ಉಪ ಆರ್ಡರ್",
      status: "ಸ್ಥಿತಿ",
      on: "ಆನ್",
      off: "ಆಫ್",
      date: "ತಾರೀಕು",
      notes: "ಟಿಪ್ಪಣಿಗಳು",
      name: "ಹೆಸರು",
      phone: "ಫೋನ್",
      email: "ಇಮೇಲ್",
      amount: "ಅಮೌಂಟ್",
      actions: "ಕ್ರಿಯೆಗಳು",
      noCol: "ನಂ.",
      pending: "ಬಾಕಿಯಿದೆ",
      paid: "ಪಾವತಿ ಮಾಡಲಾಗಿದೆ",
      firstPage: "ಮೊದಲ ಪುಟ",
      prevPage: "ಹಿಂದಿನ ಪುಟ",
      nextPage: "ಮುಂದಿನ ಪುಟ",
      lastPage: "ಕೊನೆಯ ಪುಟ",
      prev: "ಹಿಂದಿನದು",
      next: "ಮುಂದಿನದು",
      orderType: "ಆರ್ಡರ್",
      client: "ಗ್ರಾಹಕರು",
      services: "ಸೇವೆಗಳು",
      location: "ಸ್ಥಳ",
      viewAll: "ಎಲ್ಲ ನೋಡಿ",
      allStatus: "ಎಲ್ಲ ಸ್ಥಿತಿ",
      allOrders: "ಎಲ್ಲ ಆರ್ಡರ್",
      addItem: "ಐಟಂ ಸೇರಿಸಿ",
      subtotal: "ಉಪಮೊತ್ತ:",
      total: "ಒಟ್ಟು ಮೊತ್ತ:",
      addCharge: "+ ಚಾರ್ಜ್ ಸೇರಿಸಿ",
      charge: "ಚಾರ್ಜ್",
      chargeDesc: "ಚಾರ್ಜ್ ವಿವರಣ",
      qty: "ಪ್ರ.",
      unitPrice: "ಪ್ರತಿ ಬೆಲೆ (₹)",
      lineTotal: "ಸಾಲು ಮೊತ್ತ (₹)",
      noServices: "ಯಾವುದೇ ಸೇವೆಗಳಿಲ್ಲ",
      sectionCharges: "ಹೆಚ್ಚುವರಿ ಚಾರ್ಜುಗಳು",
      sectionTerms: "ನಿಯಮ ಮತ್ತು ಷರತ್ತುಗಳು",
      noTerms: "ಯಾವುದೇ ನಿಯಮ ಮತ್ತು ಷರತ್ತುಗಳು ಲಭ್ಯವಿಲ್ಲ.",
      noTermsHint: "ನಿಮ್ಮ ನಿಯಮಗಳನ್ನು ಸೇರಿಸಲು ನಿಯಮ ಪುಟ ತೆರೆಯಿರಿ.",
      showInPdf: "PDFನಲ್ಲಿ ತೋರಿಸಿ",
      showDescPdf: "PDFನಲ್ಲಿ ವಿವರಣ ತೋರಿಸಿ",
      showPaymentPdf: "PDFನಲ್ಲಿ ಪಾವತಿ ವಿವರಗಳು ತೋರಿಸಿ",
      showQrPdf: "PDFನಲ್ಲಿ QR ಕೋಡ್ ತೋರಿಸಿ",
    },
    navbar: {
      settings: "ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
      logout: "ಲಾಗ್‌ಔಟ್",
      help: "ಸಹಾಯ ಮತ್ತು ಬೆಂಬಲ",
      tutorial: "ಟ್ಯುಟೋರಿಯಲ್",
      plan: "ನಿಮ್ಮ ಯೋಜನೆ",
      paymentHistory: "ಪಾವತಿ ಇತಿಹಾಸ",
    },
    dashboard: {
      title: "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
      overview: "ಅವಲೋಕನ",
      recentActivity: "ಇತ್ತೀಚಿನ ಚಟುವಟಿಕೆ",
      welcomeBack: "ಸ್ವಾಗತ",
      todaySummary: "ಇಂದು ನಿಮ್ಮ ವ್ಯವಹಾರದಲ್ಲಿ ನಡೆಯುತ್ತಿರುವುದು ಇಲ್ಲಿದೆ.",
      filterAll: "ಎಲ್ಲ",
      filterWeek: "ವಾರ",
      filterMonth: "ತಿಂಗಳು",
      filterYear: "ವರ್ಷ",
      filterCustom: "ಕಸ್ಟಮ್",
      filterFrom: "ಇಂದ",
      filterTo: "ವರೆಗೆ",
      orderOwn: "ಸ್ವಂತ",
      orderSub: "ಉಪ ಆರ್ಡರ್",
      statTotalEvents: "ಒಟ್ಟು ಕಾರ್ಯಕ್ರಮಗಳು",
      statTotalBills: "ಒಟ್ಟು ಬಿಲ್ಲುಗಳು",
      statTotal: "ಒಟ್ಟು",
      statGross: "ಎಲ್ಲಾ ಬಿಲ್ಲಿತ ಮೊತ್ತ",
      statPending: "ಬಾಕಿಯಿದೆ",
      statOutstanding: "ಬಾಕಿ ಮೊತ್ತ",
      statPaid: "ಪಾವತಿ ಮಾಡಲಾಗಿದೆ",
      statCollected: "ಸಂಗ್ರಹಿಸಿದ ಮೊತ್ತ",
      statProfit: "ಲಾಭ",
      statProfitSub: "ಬಿಲ್ − ಚಾರ್ಜ್ − ತಂಡ ಪಾವತಿಗಳು",
      upcomingEvents: "ಮುಂದಿನ ಕಾರ್ಯಕ್ರಮಗಳು",
      eventCalendar: "ಕಾರ್ಯಕ್ರಮ ಕ್ಯಾಲೆಂಡರ್",
      calendarHint: "ಅದರ ಕಾರ್ಯಕ್ರಮಗಳನ್ನು ನೋಡಲು ದಿನವನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      showUpcoming: "ಮುಂದಿನ",
      createEvent: "ಕಾರ್ಯಕ್ರಮ ರಚಿಸಿ",
      quickActions: "ತ್ವರಿತ ಕ್ರಿಯೆಗಳು",
      noUpcomingEvents: "ಮುಂದಿನ ಕಾರ್ಯಕ್ರಮಗಳಿಲ್ಲ",
      noUpcomingHint: "ಕಾರ್ಯಕ್ರಮ ಪುಟದಿಂದ ಕಾರ್ಯಕ್ರಮಗಳನ್ನು ಸೇರಿಸಿ",
      dateTBA: "ತಾರೀಕು ತಿಳಿಯಿಲ್ಲ",
      listView: "ಪಟ್ಟಿ",
      calendarView: "ಕ್ಯಾಲೆಂಡರ್",
      viewToggleLabel: "ಮುಂದಿನ ಕಾರ್ಯಕ್ರಮಗಳ ನೋಟ",
      today: "ಇಂದು",
      tomorrow: "ನಾಳೆ",
      overdue: "ಬಾಕಿ",
      inDays: "{count} ದಿನಗಳಲ್ಲಿ",
      noEventsThisMonth: "ಈ ತಿಂಗಳು ಯಾವುದೂ ಇಲ್ಲ",
      eventsThisMonth: "ಈ ತಿಂಗಳು {count}",
      undatedNote: "ತಾರೀಕು ಇಲ್ಲದ್ದು {count}",
      noEventsOnDay: "ಯಾವುದೂ ಇಲ್ಲ",
      eventsOnDay: "{count} ಕಾರ್ಯಕ್ರಮಗಳು",
      eventSingular: "ಕಾರ್ಯಕ್ರಮ",
      untitledEvent: "ಹೆಸರಿಲ್ಲದ ಕಾರ್ಯಕ್ರಮ",
      calendarPreviewEmpty: "ಯಾವುದೇ ಕಾರ್ಯಕ್ರಮ ನಿಗದಿಯಾಗಿಲ್ಲ",
      calendarPreviewHint: "ದಿನವನ್ನು ನೋಡಲು ಕ್ಲಿಕ್ ಮಾಡಿ",
      calendarPreviewMore: "+{count} ಇನ್ನಷ್ಟು · ನೋಡಲು ಕ್ಲಿಕ್ ಮಾಡಿ",
      noEventsOnDayHint: "ಈ ದಿನಕ್ಕೆ ಏನೂ ನಿಗದಿಯಾಗಿಲ್ಲ.",
      actionQuotation: "ಕೋಟೇಶನ್",
      actionEvent: "ಕಾರ್ಯಕ್ರಮ",
      actionBilling: "ಬಿಲ್ಲಿಂಗ್",
      actionProject: "ಪ್ರಾಜೆಕ್ಟ್",
      actionTeam: "ತಂಡ",
      actionNotes: "ಟಿಪ್ಪಣಿ",
      actionService: "ಸೇವೆ",
    },
    pages: {
      quotations: "ಕೋಟೇಶನ್‌ಗಳು",
      quotationsSubtitle: "ಗ್ರಾಹಕ ಕೋಟೇಶನ್‌ಗಳನ್ನು ನಿರ್ವಹಿಸಿ ಮತ್ತು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
      events: "ಕಾರ್ಯಕ್ರಮಗಳು",
      eventsSubtitle:
        "ಗ್ರಾಹಕ ಕಾರ್ಯಕ್ರಮಗಳು ಮತ್ತು ಸೇವೆಗಳನ್ನು ನಿರ್ವಹಿಸಿ ಮತ್ತು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
      billing: "ಬಿಲ್ಲಿಂಗ್ ಮತ್ತು ಇನ್‌ವಾಯ್ಸ್‌ಗಳು",
      billingSubtitle:
        "ಗ್ರಾಹಕ ಇನ್‌ವಾಯ್ಸ್‌ಗಳು ಮತ್ತು ಪಾವತಿಗಳನ್ನು ನಿರ್ವಹಿಸಿ ಮತ್ತು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
      notes: "ಟಿಪ್ಪಣಿಗಳು",
      team: "ತಂಡ",
      teamSubtitle: "ನಿಮ್ಮ ತಂಡದ ಸದಸ್ಯರನ್ನು ಇಲ್ಲಿ ನಿರ್ವಹಿಸಿ",
      projects: "ಯೋಜನೆಗಳು",
      projectsSubtitle:
        "ನಿಮ್ಮ ಎಲ್ಲ ವ್ಯವಹಾರ ಯೋಜನೆಗಳನ್ನು ನಿರ್ವಹಿಸಿ ಮತ್ತು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
      policy: "ನಿಯಮ",
      services: "ಸೇವೆಗಳು ಮತ್ತು ಪ್ಯಾಕೇಜ್‌ಗಳು",
      servicesSubtitle: "ನಿಮ್ಮ ಸೇವಾ ಕ್ಯಾಟಲಾಗ್ ಮತ್ತು ಪ್ಯಾಕೇಜ್‌ಗಳನ್ನು ನಿರ್ವಹಿಸಿ",
      payment: "ಪಾವತಿ",
      analytics: "ವಿಶ್ಲೇಷಣೆ",
    },
    profile: {
      title: "ಪ್ರೊಫೈಲ್ ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
      fullName: "ಪೂರ್ಣ ಹೆಸರು",
      email: "ಇಮೇಲ್",
      phone: "ಫೋನ್",
      designation: "ಪದನಾಮ / ಪಾತ್ರ",
      uploadPhoto: "ಫೋಟೋ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ",
      changePhoto: "ಫೋಟೋ ಬದಲಾಯಿಸಿ",
    },
    reminder: {
      preset_120: "2 ಗಂಟೆ ಮೊದಲು",
      preset_720: "12 ಗಂಟೆ ಮೊದಲು",
      preset_1440: "24 ಗಂಟೆ ಮೊದಲು",
      preset_2880: "2 ದಿನ ಮೊದಲು",
    },
    note: {
      title: "ಟಿಪ್ಪಣಿಗಳು",
      subtitle: "ನಿಮ್ಮ ವಿಚಾರಗಳು ಮತ್ತು ಕಾರ್ಯಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
      tabNotes: "ಟಿಪ್ಪಣಿಗಳು",
      tabArchived: "ಆರ್ಕೈವ್ ಮಾಡಲಾಗಿದೆ",
      searchPlaceholder: "ಟಿಪ್ಪಣಿಗಳನ್ನು ಹುಡುಕಿ...",
      addNote: "ಹೊಸ ಟಿಪ್ಪಣಿ",
      takeANote: "ಟಿಪ್ಪಣಿ ತೆಗೆದುಕೊಳ್ಳಿ...",
      typeText: "ಟೆಕ್ಸ್ಟ್ ಟಿಪ್ಪಣಿ",
      typeChecklist: "ಚೆಕ್‌ಲಿಸ್ಟ್ ಟಿಪ್ಪಣಿ",
      titlePlaceholder: "ಶೀರ್ಷಿಕೆ",
      addItem: "ಐಟಂ ಸೇರಿಸಿ...",
      saveNote: "ಟಿಪ್ಪಣಿ ಉಳಿಸಿ",
      pinned: "ಪಿನ್ ಮಾಡಲಾಗಿದೆ",
      others: "ಇತರರು",
      loading: "ಟಿಪ್ಪಣಿಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...",
      noArchived: "ಯಾವುದೇ ಆರ್ಕೈವ್ ಟಿಪ್ಪಣಿಗಳಿಲ್ಲ",
      noNotes: "ಇನ್ನೂ ಯಾವುದೇ ಟಿಪ್ಪಣಿಗಳಿಲ್ಲ",
      archivedEmpty: "ನಿಮ್ಮ ಆರ್ಕೈವ್ ಟಿಪ್ಪಣಿಗಳು ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತವೆ",
      notesEmpty:
        '"ಟಿಪ್ಪಣಿ ತೆಗೆದುಕೊಳ್ಳಿ..." ಕ್ಲಿಕ್ ಮಾಡಿ ನಿಮ್ಮ ಮೊದಲ ಟಿಪ್ಪಣಿ ರಚಿಸಿ',
      edit: "ಎಡಿಟ್",
      archive: "ಆರ್ಕೈವ್",
      unarchive: "ಆರ್ಕೈವ್ ರದ್ದು",
      delete: "ಅಳಿಸಿ",
      noteContent: "ಟಿಪ್ಪಣಿ ವಿಷಯ...",
      saveChanges: "ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ",
    },
    policy: {
      title: "ನಿಯಮ ಮತ್ತು ಷರತ್ತುಗಳು",
      subtitle: "ನಿಮ್ಮ ಎಲ್ಲ ನಿಯಮ ಮತ್ತು ಷರತ್ತುಗಳನ್ನು ಇಲ್ಲಿ ನಿರ್ವಹಿಸಿ",
      addTerm: "+ ನಿಯಮ ಸೇರಿಸಿ",
      totalTerms: "ಒಟ್ಟು ನಿಯಮಗಳು",
      categories: "ವರ್ಗಗಳು",
      uncategorized: "ವರ್ಗೀಕರಿಸಲಾಗಿಲ್ಲ",
      groupLabel: "ಗುಂಪು/ವರ್ಗ",
      selectGroup: "-- ಗುಂಪು ಆರಿಸಿ --",
      createNewGroup: "+ ಹೊಸ ಗುಂಪು ರಚಿಸಿ...",
      enterTerm: "ನಿಯಮ ಮತ್ತು ಷರತ್ತುಗಳನ್ನು ನಮೂದಿಸಿ",
      editTerm: "ಎಡಿಟ್",
      update: "ಅಪ್‌ಡೇಟ್",
      addTermBtn: "ನಿಯಮ ಸೇರಿಸಿ",
      savedTerms: "ಉಳಿಸಿದ ನಿಯಮ ಮತ್ತು ಷರತ್ತುಗಳು",
      allGroups: "ಎಲ್ಲ ಗುಂಪುಗಳು",
      searchTerms: "ನಿಯಮಗಳನ್ನು ಹುಡುಕಿ...",
      noTerms: "ಇನ್ನೂ ಯಾವುದೇ ನಿಯಮ ಮತ್ತು ಷರತ್ತುಗಳನ್ನು ಸೇರಿಸಿಲ್ಲ.",
      noTermsHint: "ನಿಮ್ಮ ಮೊದಲ ನಿಯಮ ಸೇರಿಸಲು ಮೇಲಿನ ಫಾರ್ಮ್ ಬಳಸಿ.",
      noMatch: "ನಿಮ್ಮ ಹುಡುಕಾಟ ಅಥವಾ ಫಿಲ್ಟರ್‌ಗೆ ಯಾವುದೇ ನಿಯಮಗಳು ಹೊಂದಿಕೆಯಾಗಲಿಲ್ಲ.",
      noMatchHint: "ನಿಮ್ಮ ಹುಡುಕಾಟ ಅಥವಾ ಫಿಲ್ಟರ್ ಮಾನದಂಡಗಳನ್ನು ಸರಿಹೊಂದಿಸಿ.",
      contentHeader: "ವಿಷಯ",
      actionsHeader: "ಕ್ರಿಯೆಗಳು",
      noHeader: "ನಂ.",
      writeTerm: "ನಿಯಮ ಮತ್ತು ಷರತ್ತುಗಳನ್ನು ಇಲ್ಲಿ ಬರೆಯಿರಿ...",
      expandAll: "ಎಲ್ಲವನ್ನು ವಿಸ್ತರಿಸಿ",
      collapseAll: "ಎಲ್ಲವನ್ನು ಮಡಚಿ",
      expandGroup: "ಗುಂಪನ್ನು ವಿಸ್ತರಿಸಿ",
      collapseGroup: "ಗುಂಪನ್ನು ಮಡಚಿ",
    },
    services: {
      subtitle: "ನಿಮ್ಮ ಎಲ್ಲ ಸೇವೆಗಳು ಮತ್ತು ಕೊಡುಗೆಗಳನ್ನು ಇಲ್ಲಿ ನಿರ್ವಹಿಸಿ",
      addNew: "ಹೊಸ ಸೇವೆ ಸೇರಿಸಿ",
      totalServices: "ಒಟ್ಟು ಸೇವೆಗಳು",
      totalCategories: "ಒಟ್ಟು ವರ್ಗಗಳು",
      totalValue: "ಒಟ್ಟು ಮೌಲ್ಯ",
      averagePrice: "ಸರಾಸರಿ ಬೆಲೆ",
      noFound: "ಯಾವುದೇ ಸೇವೆಗಳು ಕಂಡುಬಂದಿಲ್ಲ",
      noFoundHint: "ನಿಮ್ಮ ಫಿಲ್ಟರ್‌ಗಳನ್ನು ಸರಿಹೊಂದಿಸಿ ಅಥವಾ ಹೊಸ ಸೇವೆ ಸೇರಿಸಿ",
      categoryLabel: "ವರ್ಗ:",
      allCategories: "ಎಲ್ಲ ವರ್ಗಗಳು",
      startingAt: "ಇಂದಿನಿಂದ",
      showing: "ತೋರಿಸಲಾಗುತ್ತಿದೆ",
      to: "ರಿಂದ",
      of: "ಒಟ್ಟು",
      servicesLabel: "ಸೇವೆಗಳು",
      perPage: "ಪ್ರತಿ ಪುಟ:",
      editTitle: "ಸೇವೆ ಎಡಿಟ್",
      addTitle: "ಹೊಸ ಸೇವೆ ಸೇರಿಸಿ",
      nameLabel: "ಸೇವೆ ಹೆಸರು",
      categoryFormLabel: "ವರ್ಗ",
      priceLabel: "ಮೂಲ ಬೆಲೆ (₹)",
      descLabel: "ವಿವರಣೆ (ಕಡ್ಡಾಯವಿಲ್ಲ)",
      selectCategory: "-- ವರ್ಗ ಆರಿಸಿ --",
      addNewCategory: "+ ಹೊಸ ವರ್ಗ ಸೇರಿಸಿ",
      enterCategoryName: "ಹೊಸ ವರ್ಗದ ಹೆಸರು ನಮೂದಿಸಿ",
      nameRequired: "ಸೇವೆ ಹೆಸರು ಅಗತ್ಯ",
      categoryRequired: "ವರ್ಗ ಅಗತ್ಯ",
      priceRequired: "ಮಾನ್ಯ ಬೆಲೆ ಅಗತ್ಯ",
      update: "ಅಪ್‌ಡೇಟ್",
      save: "ಉಳಿಸಿ",
    },
    event: {
      newEvent: "ಹೊಸ ಕಾರ್ಯಕ್ರಮ",
      statTotal: "ಒಟ್ಟು ಕಾರ್ಯಕ್ರಮಗಳು",
      statCompleted: "ಪೂರ್ಣಗೊಂಡಿದೆ",
      statCancelled: "ರದ್ದುಗೊಂಡಿದೆ",
      statScheduled: "ನಿಗದಿತ / ಪ್ರಗತಿಯಲ್ಲಿದೆ",
      searchPlaceholder: "ಗ್ರಾಹಕ, ID, ಸೇವೆ ಅಥವಾ ಸ್ಥಳದಿಂದ ಕಾರ್ಯಕ್ರಮ ಹುಡುಕಿ...",
      statusScheduled: "ನಿಗದಿತ",
      statusInProgress: "ಪ್ರಗತಿಯಲ್ಲಿದೆ",
      statusCompleted: "ಪೂರ್ಣಗೊಂಡಿದೆ",
      statusCancelled: "ರದ್ದುಗೊಂಡಿದೆ",
      orderLabel: "ಆರ್ಡರ್",
      colEventId: "ಕಾರ್ಯಕ್ರಮ ID",
      colQuotNo: "ಕೋಟೇಶನ್ ನಂ.",
      colTeam: "ತಂಡ",
      emptyTitle: "ಯಾವುದೇ ಕಾರ್ಯಕ್ರಮಗಳಿಲ್ಲ",
      emptyHint: "ಪ್ರಾರಂಭಿಸಲು ನಿಮ್ಮ ಮೊದಲ ಕಾರ್ಯಕ್ರಮ ರಚಿಸಿ",
      eventsLabel: "ಕಾರ್ಯಕ್ರಮಗಳು",
      createTitle: "ಕಾರ್ಯಕ್ರಮ ರಚಿಸಿ",
      labelEventId: "ಕಾರ್ಯಕ್ರಮ ID",
      labelQuotationId: "ಕೋಟೇಶನ್ ID",
      searchQuotation: "ID ಅಥವಾ ಗ್ರಾಹಕ ಹೆಸರಿನಿಂದ ಕೋಟೇಶನ್ ಹುಡುಕಿ",
      noQuotations: "ಯಾವುದೇ ಕೋಟೇಶನ್‌ಗಳಿಲ್ಲ",
      labelClientName: "ಗ್ರಾಹಕ ಹೆಸರು *",
      enterClientName: "ಗ್ರಾಹಕ ಹೆಸರು ನಮೂದಿಸಿ",
      labelClientPhone: "ಗ್ರಾಹಕ ಫೋನ್ *",
      labelClientEmail: "ಗ್ರಾಹಕ ಇಮೇಲ್",
      sectionServices: "ಸೇವೆಗಳು ಮತ್ತು ವೇಳಾಪಟ್ಟಿ",
      addService: "ಸೇವೆ ಸೇರಿಸಿ",
      labelServiceType: "ಸೇವೆ ವಿಧ",
      labelTime: "ಸಮಯ",
      enterLocation: "ಕಾರ್ಯಕ್ರಮ ಸ್ಥಳ ನಮೂದಿಸಿ",
      notesPlaceholder: "ಈ ಸೇವೆಗೆ ಹೆಚ್ಚುವರಿ ಟಿಪ್ಪಣಿಗಳು",
      sameAsAbove: "ಮೇಲಿನಂತೆ ಅದೇ ತಾರೀಕು/ಸಮಯ/ಸ್ಥಳ",
      sectionTeam: "ತಂಡ ನಿಯೋಜನೆ",
      notifyAssignees: "ಸದಸ್ಯರಿಗೆ ತಿಳಿಸಿ",
      notifyAssigneesHint:
        "ನಿಯೋಜಿತ ಸದಸ್ಯರಿಗೆ ಅಧಿಸೂಚನೆ ಕಳುಹಿಸಿ (ಪೂರ್ವನಿಯೋಜಿತವಾಗಿ ಆನ್). ಬಿಟ್ಟುಬಿಡಲು ಆಫ್ ಮಾಡಿ.",
      colReferredBy: "ಆಹ್ವಾನಿಸಿದವರು",
      labelReferredBy: "ಆಹ್ವಾನಿಸಿದವರು",
      referredByPlaceholder:
        "ಈ ಸಬ್-ಆರ್ಡರ್ ಅನ್ನು ಯಾರು ಆಹ್ವಾನಿಸಿದ್ದಾರೆಂದು ನಮೂದಿಸಿ",
      colInvitedBy: "ಆಹ್ವಾನಿಸಿದವರು",
      labelInvitedBy: "ಆಹ್ವಾನಿಸಿದವರು",
      invitedByPlaceholder:
        "ಈ ಸಬ್-ಆರ್ಡರ್ ಅನ್ನು ಯಾರು ಆಹ್ವಾನಿಸಿದ್ದಾರೆಂದು ನಮೂದಿಸಿ",
      sectionInvitedBy: "ಆಹ್ವಾನಿಸಿದವರು",
      noTeamMembers: "ತಂಡದ ಸದಸ್ಯರಿಲ್ಲ. ತಂಡ ಪುಟದಲ್ಲಿ ಸದಸ್ಯರನ್ನು ಸೇರಿಸಿ.",
      createEvent: "ಕಾರ್ಯಕ್ರಮ ರಚಿಸಿ",
      viewQuotation: "ಕೋಟೇಶನ್:",
      labelNA: "NA",
      labelCreated: "ರಚಿಸಲಾಗಿದೆ",
      labelCreatedDate: "ರಚಿಸಿದ ತಾರೀಕು:",
      labelOrderTypeView: "ಆರ್ಡರ್ ವಿಧ:",
      serviceCount: "ಸೇವೆಗಳು",
      svcNotes: "ಟಿಪ್ಪಣಿಗಳು:",
      teamMembersSection: "ತಂಡದ ಸದಸ್ಯರು",
      pingTeam: "ತಂಡಕ್ಕೆ ತಿಳಿಸಿ",
      pinging: "ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ...",
      pingTeamHint: "ಎಲ್ಲಾ ನಿಯೋಜಿತ ಸದಸ್ಯರಿಗೆ ಅಧಿಸೂಚನೆಯನ್ನು ಮತ್ತೆ ಕಳುಹಿಸಿ",
      pingMemberHint: "ಅಧಿಸೂಚನೆಯನ್ನು ಮತ್ತೆ ಕಳುಹಿಸಿ",
    },
    quotation: {
      newQuotation: "ಹೊಸ ಕೋಟೇಶನ್",
      statTotal: "ಒಟ್ಟು ಕೋಟೇಶನ್‌ಗಳು",
      statRejected: "ತಿರಸ್ಕರಿಸಲಾಗಿದೆ",
      statRejectedValue: "ತಿರಸ್ಕೃತ ಮೌಲ್ಯ",
      statPending: "ಬಾಕಿಯಿದೆ",
      statPendingValue: "ಬಾಕಿ ಮೌಲ್ಯ",
      statApproved: "ಅನುಮೋದಿಸಲಾಗಿದೆ",
      statApprovedValue: "ಅನುಮೋದಿತ ಮೌಲ್ಯ",
      statRejValue: "ತಿರಸ್ಕೃತ ಮೌಲ್ಯ",
      statPendValue: "ಬಾಕಿ ಮೌಲ್ಯ",
      statAppValue: "ಅನುಮೋದಿತ ಮೌಲ್ಯ",
      statTotalValue: "ಒಟ್ಟು ಮೌಲ್ಯ",
      searchPlaceholder: "ID, ಹೆಸರು ಅಥವಾ ತಾರೀಕಿನಿಂದ ಕೋಟೇಶನ್ ಹುಡುಕಿ...",
      statusPending: "ಬಾಕಿಯಿದೆ",
      statusApproved: "ಅನುಮೋದಿಸಲಾಗಿದೆ",
      statusRejected: "ತಿರಸ್ಕರಿಸಲಾಗಿದೆ",
      colQuotId: "ಕೋಟೇಶನ್ ID",
      colEventDate: "ಕಾರ್ಯಕ್ರಮ ತಾರೀಕು",
      colAmount: "ಅಮೌಂಟ್",
      colMainEventDate: "ಮುಖ್ಯ ಕಾರ್ಯಕ್ರಮ ತಾರೀಕು",
      emptyTitle: "ಯಾವುದೇ ಕೋಟೇಶನ್‌ಗಳಿಲ್ಲ",
      emptyHint: "ಪ್ರಾರಂಭಿಸಲು ನಿಮ್ಮ ಮೊದಲ ಕೋಟೇಶನ್ ರಚಿಸಿ",
      quotationsLabel: "ಕೋಟೇಶನ್‌ಗಳು",
      createTitle: "ಕೋಟೇಶನ್ ರಚಿಸಿ",
      editTitle: "ಕೋಟೇಶನ್ ಎಡಿಟ್",
      orderOwn: "ಸ್ವಂತ",
      orderSub: "ಉಪ",
      labelClientName: "ಗ್ರಾಹಕ ಹೆಸರು *",
      labelClientPhone: "ಗ್ರಾಹಕ ಫೋನ್ *",
      labelClientEmail: "ಗ್ರಾಹಕ ಇಮೇಲ್",
      labelEventDate: "ಮುಖ್ಯ ಕಾರ್ಯಕ್ರಮ ತಾರೀಕು",
      enterClientName: "ಗ್ರಾಹಕ ಹೆಸರು ನಮೂದಿಸಿ",
      enterNumbersOnly: "ಸಂಖ್ಯೆಗಳು ಮಾತ್ರ ನಮೂದಿಸಿ",
      colService: "ಸೇವೆ",
      colQty: "ಪ್ರಮಾಣ",
      colPrice: "ಬೆಲೆ (₹)",
      colTotal: "ಒಟ್ಟು",
      colDescOpt: "ವಿವರಣೆ (ಕಡ್ಡಾಯವಿಲ್ಲ)",
      additionalDetails: "ಈ ಸೇವೆಯ ಹೆಚ್ಚುವರಿ ವಿವರಗಳು",
      termsSelected: "ನಿಯಮ(ಗಳು) ಆಯ್ಕೆಯಾಗಿದೆ",
      createQuotation: "ಕೋಟೇಶನ್ ರಚಿಸಿ",
      viewTitle: "ಕೋಟೇಶನ್",
      colUnitPrice: "ಪ್ರತಿ ಬೆಲೆ (₹)",
      colLineTotal: "ಸಾಲು ಮೊತ್ತ (₹)",
      approveBtn: "ಅನುಮೋದಿಸಿ",
      rejectBtn: "ತಿರಸ್ಕರಿಸಿ",
      colQtyShort: "ಪ್ರ.",
    },
    billing: {
      newBill: "ಹೊಸ ಬಿಲ್",
      statTotalAmount: "ಒಟ್ಟು ಮೊತ್ತ",
      statPartialPayment: "ಭಾಗಶಃ ಪಾವತಿ",
      statPending: "ಬಾಕಿ ಪಾವತಿ",
      statPaid: "ಒಟ್ಟು ಪಾವತಿ",
      searchPlaceholder:
        "ID, ಹೆಸರು, ತಾರೀಕು, ಕೋಟೇಶನ್ ID, ಸೇವೆ ಅಥವಾ ಮೊತ್ತದಿಂದ ಹುಡುಕಿ...",
      statusPending: "ಬಾಕಿಯಿದೆ",
      statusPartial: "ಭಾಗಶಃ",
      statusPaid: "ಪಾವತಿಯಾಗಿದೆ",
      statusOverdue: "ಅವಧಿ ಮೀರಿದೆ",
      orderOwn: "ಸ್ವಂತ",
      orderSub: "ಉಪ",
      colInvoiceId: "ಇನ್‌ವಾಯ್ಸ್ ID",
      colEventId: "ಕಾರ್ಯಕ್ರಮ ID",
      colQuotId: "ಕೋಟೇಶನ್ ID",
      colBalance: "ಬಾಕಿ",
      emptyTitle: "ಯಾವುದೇ ಇನ್‌ವಾಯ್ಸ್‌ಗಳಿಲ್ಲ",
      emptyHint: "ನಿಮ್ಮ ಫಿಲ್ಟರ್‌ಗಳನ್ನು ಸರಿಹೊಂದಿಸಿ",
      invoicesLabel: "ಇನ್‌ವಾಯ್ಸ್‌ಗಳು",
      colTotalAmt: "ಒಟ್ಟು ಮೊತ್ತ",
      colPaymentMethod: "ಪಾವತಿ ವಿಧಾನ",
      viewBtn: "ನೋಡಿ",
      recordPaymentTitle: "ಪಾವತಿ ದಾಖಲಿಸಿ",
      labelTotalAmt: "ಒಟ್ಟು ಮೊತ್ತ:",
      labelAlreadyPaid: "ಈಗಾಗಲೇ ಪಾವತಿ:",
      labelOutstanding: "ಬಾಕಿ ಮೊತ್ತ:",
      labelPaymentAmt: "ಪಾವತಿ ಮೊತ್ತ (₹) *",
      labelPaymentMethod: "ಪಾವತಿ ವಿಧಾನ *",
      labelPaymentDate: "ಪಾವತಿ ತಾರೀಕು *",
      methodBankTransfer: "ಬ್ಯಾಂಕ್ ವರ್ಗಾವಣೆ",
      methodUpi: "UPI",
      methodCash: "ನಗದು",
      methodCheque: "ಚೆಕ್",
      recordPaymentBtn: "ಪಾವತಿ ದಾಖಲಿಸಿ",
      createTitle: "ಇನ್‌ವಾಯ್ಸ್ ರಚಿಸಿ",
      editTitle: "ಇನ್‌ವಾಯ್ಸ್ ಎಡಿಟ್",
      qtnMode: "QTN ID",
      evntMode: "EVNT ID",
      labelInvoiceId: "ಇನ್‌ವಾಯ್ಸ್ ID",
      labelIssueDate: "ನೀಡಿದ ತಾರೀಕು",
      labelClientName: "ಗ್ರಾಹಕ ಹೆಸರು *",
      labelClientPhone: "ಗ್ರಾಹಕ ಫೋನ್ *",
      labelClientEmail: "ಗ್ರಾಹಕ ಇಮೇಲ್",
      searchQuotation: "ID ಅಥವಾ ಗ್ರಾಹಕ ಹೆಸರಿನಿಂದ ಕೋಟೇಶನ್ ಹುಡುಕಿ",
      searchEvent: "ID ಅಥವಾ ಗ್ರಾಹಕ ಹೆಸರಿನಿಂದ ಕಾರ್ಯಕ್ರಮ ಹುಡುಕಿ",
      enterClientName: "ಗ್ರಾಹಕ ಹೆಸರು ನಮೂದಿಸಿ",
      enterNumbersOnly: "ಸಂಖ್ಯೆಗಳು ಮಾತ್ರ ನಮೂದಿಸಿ",
      noQuotations: "ಯಾವುದೇ ಕೋಟೇಶನ್‌ಗಳಿಲ್ಲ",
      noEvents: "ಯಾವುದೇ ಕಾರ್ಯಕ್ರಮಗಳಿಲ್ಲ",
      sectionServices: "ಸೇವೆಗಳು",
      createInvoice: "ಇನ್‌ವಾಯ್ಸ್ ರಚಿಸಿ",
      labelIssueDate2: "ನೀಡಿದ ತಾರೀಕು:",
      noCharges: "ಯಾವುದೇ ಹೆಚ್ಚುವರಿ ಚಾರ್ಜ್‌ಗಳಿಲ್ಲ.",
      labelTotalAmt2: "ಒಟ್ಟು ಮೊತ್ತ:",
      labelAmountPaid: "ಪಾವತಿ ಮೊತ್ತ:",
      labelBalanceDue: "ಬಾಕಿ ಮೊತ್ತ:",
      repaymentHistory: "ಮರುಪಾವತಿ ಇತಿಹಾಸ",
      labelRef: "Ref:",
      noPayments: "ಯಾವುದೇ ಪಾವತಿಗಳನ್ನು ದಾಖಲಿಸಿಲ್ಲ.",
      downloadPdf: "PDF ಡೌನ್‌ಲೋಡ್",
      sendEmail: "ಇಮೇಲ್ ಕಳುಹಿಸಿ",
      noServiceItems: "ಯಾವುದೇ ಸೇವೆ ಐಟಂಗಳಿಲ್ಲ.",
      colEventIdView: "ಕಾರ್ಯಕ್ರಮ ID:",
      colQuotIdView: "ಕೋಟೇಶನ್ ID:",
    },
    team: {
      addMember: "+ ತಂಡ ಸದಸ್ಯ ಸೇರಿಸಿ",
      statTotal: "ಒಟ್ಟು ಸದಸ್ಯರು",
      statPayment: "ಒಟ್ಟು ಪಾವತಿ",
      statPaid: "ಪಾವತಿ ಮೊತ್ತ",
      statPending: "ಬಾಕಿ ಮೊತ್ತ",
      searchPlaceholder: "ಹೆಸರು, ಪಾತ್ರ ಅಥವಾ ಫೋನ್‌ನಿಂದ ಹುಡುಕಿ...",
      emptyState: "ಯಾವುದೇ ತಂಡ ಸದಸ್ಯರಿಲ್ಲ.",
      emptyTitle: "ಯಾವುದೇ ತಂಡ ಸದಸ್ಯರಿಲ್ಲ.",
      editMember: "ತಂಡ ಸದಸ್ಯ ಎಡಿಟ್",
      deleteMember: "ಅಳಿಸಿ",
      addTitle: "ತಂಡ ಸದಸ್ಯ ಸೇರಿಸಿ",
      addMemberTitle: "ತಂಡ ಸದಸ್ಯ ಸೇರಿಸಿ",
      editTitle: "ತಂಡ ಸದಸ್ಯ ಎಡಿಟ್",
      labelName: "ಹೆಸರು",
      labelPhone: "ಫೋನ್",
      labelEmail: "ಇಮೇಲ್",
      labelRole: "ಪಾತ್ರ",
      addPaymentTitle: "ಪಾವತಿ ಸೇರಿಸಿ",
      labelEvent: "ಕಾರ್ಯಕ್ರಮ",
      useCustomEvent: "ಕಸ್ಟಮ್ ಕಾರ್ಯಕ್ರಮ ಹೆಸರು ಬಳಸಿ",
      selectEvent: "ಕಾರ್ಯಕ್ರಮ ಆರಿಸಿ",
      noEventsForMember: "ಈ ಸದಸ್ಯರಿಗೆ ಕಾರ್ಯಕ್ರಮಗಳಿಲ್ಲ",
      enterEventName: "ಕಾರ್ಯಕ್ರಮ ಹೆಸರು ನಮೂದಿಸಿ",
      labelAmount: "ಅಮೌಂಟ್ (₹)",
      labelPaymentDate: "ಪಾವತಿ ತಾರೀಕು",
      labelStatus: "ಸ್ಥಿತಿ",
      labelNotes: "ಟಿಪ್ಪಣಿಗಳು (ಕಡ್ಡಾಯವಿಲ್ಲ)",
      statusPending: "ಬಾಕಿಯಿದೆ",
      statusPartial: "ಭಾಗಶಃ",
      statusPaid: "ಪಾವತಿಯಾಗಿದೆ",
      notesPlaceholder: "ಈ ಪಾವತಿಯ ಬಗ್ಗೆ ಟಿಪ್ಪಣಿ ಸೇರಿಸಿ...",
      addPaymentBtn: "ಪಾವತಿ ಸೇರಿಸಿ",
      paymentsTitle: "ಪಾವತಿಗಳು",
      colEventId: "ಕಾರ್ಯಕ್ರಮ ID",
      colEvent: "ಕಾರ್ಯಕ್ರಮ",
      colPayment: "ಪಾವತಿ",
      colPaymentDate: "ಪಾವತಿ ತಾರೀಕು",
      colStatus: "ಸ್ಥಿತಿ",
      colNotes: "ಟಿಪ್ಪಣಿಗಳು",
      colActions: "ಕ್ರಿಯೆಗಳು",
      markPaid: "ಪಾವತಿ ಗುರುತಿಸಿ",
      noPayments: "ಈ ಸದಸ್ಯರಿಗೆ ಯಾವುದೇ ಪಾವತಿ ದಾಖಲಿಸಿಲ್ಲ.",
      pageLabel: "ಪುಟ",
      ofLabel: "ರಲ್ಲಿ",
    },
    project: {
      newProject: "ಹೊಸ ಯೋಜನೆ",
      searchPlaceholder: "ಯೋಜನೆಗಳನ್ನು ಹುಡುಕಿ...",
      projectsLabel: "ಯೋಜನೆಗಳು",
      colClient: "ಗ್ರಾಹಕ:",
      colDeadline: "ಗಡುವು:",
      colDescription: "ವಿವರಣೆ:",
      noDescription: "ಯಾವುದೇ ವಿವರಣೆ ಇಲ್ಲ",
      noDescHint: "ಯಾವುದೇ ವಿವರಣೆ ಇಲ್ಲ",
      viewDetails: "ವಿವರ ನೋಡಿ",
      emptyState: "ನಿಮ್ಮ ಮಾನದಂಡಕ್ಕೆ ಹೊಂದಿಕೆಯಾಗುವ ಯಾವುದೇ ಯೋಜನೆಗಳಿಲ್ಲ.",
      emptyTitle: "ನಿಮ್ಮ ಮಾನದಂಡಕ್ಕೆ ಹೊಂದಿಕೆಯಾಗುವ ಯಾವುದೇ ಯೋಜನೆಗಳಿಲ್ಲ.",
      addTitle: "ಹೊಸ ಯೋಜನೆ ಸೇರಿಸಿ",
      viewTitle: "ಯೋಜನೆ ವಿವರಗಳು",
      editTitle: "ಯೋಜನೆ ಎಡಿಟ್",
      selectEvent: "ಅಸ್ತಿತ್ವದಲ್ಲಿರುವ ಕಾರ್ಯಕ್ರಮ ಆರಿಸಿ",
      searchEvent: "ID ಅಥವಾ ಗ್ರಾಹಕ ಹೆಸರಿನಿಂದ ಕಾರ್ಯಕ್ರಮ ಹುಡುಕಿ",
      eventHint:
        "ಕಾರ್ಯಕ್ರಮ ಆಯ್ಕೆ ಮಾಡಿದರೆ ಯೋಜನೆ ಮತ್ತು ಗ್ರಾಹಕ ಹೆಸರು ತಂತಾನೇ ತುಂಬುತ್ತದೆ. ಎರಡನ್ನೂ ಸಂಪಾದಿಸಬಹುದು.",
      labelProjectName: "ಯೋಜನೆ ಹೆಸರು *",
      labelClientName: "ಗ್ರಾಹಕ ಹೆಸರು *",
      labelStatus: "ಯೋಜನೆ ಸ್ಥಿತಿ *",
      labelDeadline: "ಗಡುವು *",
      labelDeadlineTime: "ಗಡುವಿನ ಸಮಯ",
      labelDescription: "ವಿವರಣೆ:",
      descPlaceholder: "ಯೋಜನೆ ವಿವರಣೆ...",
      createProject: "ಯೋಜನೆ ರಚಿಸಿ",
      deleteProject: "ಯೋಜನೆ ಅಳಿಸಿ",
      editProject: "ಯೋಜನೆ ಎಡಿಟ್",
      close: "ಮುಚ್ಚಿ",
      labelClient: "ಗ್ರಾಹಕ",
      labelDeadlineView: "ಗಡುವು",
      page: "ಪುಟ",
    },

    planCard: {
      silver: "ಸಿಲ್ವರ್",
      gold: "ಗೋಲ್ಡ್",
      unlimitedFeatures: "ಅನಿಯಮಿತ ವೈಶಿಷ್ಟ್ಯಗಳು",
      limitedFeatures: "ಸೀಮಿತ ವೈಶಿಷ್ಟ್ಯಗಳು",
      save: "ಉಳಿಸಿ",
      off: "ರಿಯಾಯಿತಿ",
      perYear: "ವರ್ಷಕ್ಕೆ",
      perMonth: "ತಿಂಗಳಿಗೆ",
      contactUs: "ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ",
      activate: "ಸಕ್ರಿಯಗೊಳಿಸಿ",
      upgradeTo: "ಅಪ್‌ಗ್ರೇಡ್ ಮಾಡಿ",
      downgradeTo: "ಡೌನ್‌ಗ್ರೇಡ್ ಮಾಡಿ",
      managePlan: "ಯೋಜನೆ ನಿರ್ವಹಿಸಿ",
      confirmUpgrade: "ನೀವು ಗೋಲ್ಡ್ ಯೋಜನೆಗೆ ಅಪ್‌ಗ್ರೇಡ್ ಮಾಡಲು ಖಚಿತವಾಗಿದ್ದೀರಾ?",
      confirmDowngrade:
        "ನೀವು ಸಿಲ್ವರ್ ಯೋಜನೆಗೆ ಡೌನ್‌ಗ್ರೇಡ್ ಮಾಡಲು ಖಚಿತವಾಗಿದ್ದೀರಾ?",
      confirmChange:
        "ನೀವು ನಿಮ್ಮ ಯೋಜನೆಯನ್ನು {from} ನಿಂದ {to} ಗೆ ಬದಲಾಯಿಸಲು ಖಚಿತವಾಗಿದ್ದೀರಾ?",
      alreadyActive: "{plan} ಯೋಜನೆ ಈಗಾಗಲೇ ನಿಮ್ಮ ಖಾತೆಯಲ್ಲಿ ಸಕ್ರಿಯವಾಗಿದೆ.",
      dialogUpgradeTitle: "ಯೋಜನೆ ಅಪ್‌ಗ್ರೇಡ್",
      dialogDowngradeTitle: "ಯೋಜನೆ ಡೌನ್‌ಗ್ರೇಡ್",
      dialogChangePlanTitle: "ಯೋಜನೆ ಬದಲಿಸಿ",
      dialogActivePlanTitle: "ಯೋಜನೆ ಸಕ್ರಿಯ",
      confirm: "ದೃಢೀಕರಿಸಿ",
      upgrade: "ಅಪ್‌ಗ್ರೇಡ್",
      downgrade: "ಡೌನ್‌ಗ್ರೇಡ್",
      ok: "ಸರಿ",
    },

    payment: {
      silver: "ಸಿಲ್ವರ್",
      gold: "ಗೋಲ್ಡ್",
      silverPlan: "ಸಿಲ್ವರ್ ಯೋಜನೆ",
      goldPlan: "ಗೋಲ್ಡ್ ಯೋಜನೆ",
      silverFeature1: "ಅನಿಯಮಿತ ಸೇವೆಗಳನ್ನು ಸೇರಿಸಿ",
      silverFeature2: "ಅನಿಯಮಿತ ತಂಡದ ಸದಸ್ಯರನ್ನು ಸೇರಿಸಿ",
      silverFeature3: "ಅನಿಯಮಿತ ಟಿಪ್ಪಣಿ ರಚನೆ ಮತ್ತು ಟ್ರ್ಯಾಕಿಂಗ್",
      silverFeature4: "ಅನಿಯಮಿತ ಕಾರ್ಯಕ್ರಮ ರಚನೆ ಮತ್ತು ಟ್ರ್ಯಾಕಿಂಗ್",
      silverFeature5: "ಅನಿಯಮಿತ ಯೋಜನೆ ರಚನೆ ಮತ್ತು ಟ್ರ್ಯಾಕಿಂಗ್",
      goldFeature1: "ಎಲ್ಲಾ ಸಿಲ್ವರ್ ಪ್ರಯೋಜನಗಳು",
      goldFeature2: "ಅನಿಯಮಿತ ನಿಯಮ ರಚನೆ",
      goldFeature3: "ಹೆಚ್ಚಿನ ಆದ್ಯತೆ ಮತ್ತು ಗ್ರಾಹಕರ ಬೆಂಬಲ",
      goldFeature4: "ಅನಿಯಮಿತ ಬಿಲ್ ರಚನೆ ಮತ್ತು ಟ್ರ್ಯಾಕಿಂಗ್",
      goldFeature5: "ಅನಿಯಮಿತ ಕೋಟೇಶನ್ ರಚನೆ ಮತ್ತು ಟ್ರ್ಯಾಕಿಂಗ್",
      periodText: "ವರ್ಷಕ್ಕೆ (ಎಲ್ಲಾ ತೆರಿಗೆ ಮತ್ತು ಶುಲ್ಕಗಳು ಒಳಗೊಂಡಿವೆ)",
      billingText: "ವಾರ್ಷಿಕ ಬಿಲ್ಲಿಂಗ್",
      formName: "ಹೆಸರು",
      formEmail: "ಇಮೇಲ್",
      formPhone: "ದೂರವಾಣಿ",
      proceedPayment: "ಪಾವತಿಗೆ ಮುಂದುವರಿಸಿ",
      processing: "ಪ್ರಕ್ರಿಯೆಗೊಳ್ಳುತ್ತಿದೆ...",
      paymentSuccess: "ಪಾವತಿ ಯಶಸ್ವಿಯಾಗಿದೆ!",
      redirecting: "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ಗೆ ಮರುನಿರ್ದೇಶಿಸಲಾಗುತ್ತಿದೆ...",
      configError:
        "ಪಾವತಿ ಗೇಟ್‌ವೇ ಕಾನ್ಫಿಗರ್ ಆಗಿಲ್ಲ. ದಯವಿಟ್ಟು ಬೆಂಬಲವನ್ನು ಸಂಪರ್ಕಿಸಿ.",
      save: "ಉಳಿಸಿ",
      back: "ಹಿಂದೆ",
      managePlan: "ಯೋಜನೆ ನಿರ್ವಹಿಸಿ",
      orderSummary: "ಆರ್ಡರ್ ಸಾರಾಂಶ",
      selectedPlan: "ಆಯ್ದ ಯೋಜನೆ",
      billingCycle: "ಬಿಲ್ಲಿಂಗ್ ಚಕ್ರ",
      annual: "ವಾರ್ಷಿಕ",
      billingInfo: "ಬಿಲ್ಲಿಂಗ್ ಮಾಹಿತಿ",
      billingInfoDesc: "Razorpay ರಸೀದಿಗಾಗಿ ಈ ವಿವರಗಳನ್ನು ಬಳಸುತ್ತದೆ.",
      payableAmt: "ಪಾವತಿಸಬೇಕಾದ ಮೊತ್ತ",
      payableAmtSub: "(ಎಲ್ಲಾ ತೆರಿಗೆ ಮತ್ತು ಶುಲ್ಕಗಳು ಒಳಗೊಂಡಿವೆ)",
      iAgree: "ನಾನು ಒಪ್ಪುತ್ತೇನೆ",
      termsAndConds: "ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು",
      pciCompliant: "PCI-DSS ಅನುಸರಣೀಯ",
      securePowered: "🔒 Razorpay ಮೂಲಕ ಸುರಕ್ಷಿತ ಪಾವತಿ.",
      subActive: "ಚಂದಾದಾರಿಕೆ ಸಕ್ರಿಯ!",
      planActivated:
        "ನಿಮ್ಮ {plan} ಯೋಜನೆ ಯಶಸ್ವಿಯಾಗಿ ಸಕ್ರಿಯಗೊಂಡಿದೆ. ಎಲ್ಲಾ ವೈಶಿಷ್ಟ್ಯಗಳನ್ನು ಆನಂದಿಸಿ.",
      goDashboard: "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ಗೆ ಹೋಗಿ",
      initializing: "ಪ್ರಾರಂಭಿಸಲಾಗುತ್ತಿದೆ...",
      securelyPay: "ಸುರಕ್ಷಿತವಾಗಿ ಪಾವತಿಸಿ",
    },
  },
};

export default translations;
